import {
  APP_INITIALIZER,
  ApplicationConfig,
  forwardRef,
  importProvidersFrom,
  inject,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withDebugTracing, withHashLocation, withPreloading } from '@angular/router';
import { appRoutes } from './app.routes';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { ApiModule } from './api/api.module';
import { DialogsModule } from './dialogs/dialogs.module';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import * as ecSimpleTransform from 'echarts-simple-transform';
import { FlexModule } from '@angular/flex-layout';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ApiInterceptor } from './core/interceptors/api-interceptor';
import { CookieService } from 'ngx-cookie-service';
import { AuthGuard } from './core/security/auth-guard.service';
import { AuthService } from './core/security/auth.service';
import { EventsService } from './events/events.service';
import { HeaderService } from './core/services/header.service';
import { RouterHelper } from './core/utilities/router-helper';
import { NotificationService } from './notification/notification.service';
import { GoogleAnalyticsService } from './analytics/google-analytics.service';
import { LocalNotificationsService } from './core/services/local-notifications.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DelayedPreloadingStrategy } from './core/preload-strategies/delayed-preloading-strategy';
import { ApiConfiguration } from './api/api-configuration';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
} from 'echarts/components';
import { providePrimeNG } from 'primeng/config';
import { MyPreset } from './primeng-preset';
import { HelpscoutService } from './core/services/helpscout.service';

echarts.registerTransform(ecSimpleTransform.aggregate);
echarts.use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  TitleComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
  PieChart,
]);

export const API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  // useClass: ApiInterceptor,
  useExisting: forwardRef(() => ApiInterceptor),
  multi: true,
};

export function initApiConfiguration(config: ApiConfiguration): Function {
  console.log('app initializer');
  const cfg = inject(EventsService);
  cfg.connect();

  return () => {
    config.rootUrl = '';
  };
}

export const INIT_API_CONFIGURATION: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initApiConfiguration,
  deps: [ApiConfiguration],
  multi: true,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withPreloading(DelayedPreloadingStrategy), withDebugTracing(), withHashLocation()),
    provideEchartsCore({ echarts }),
    providePrimeNG({
      theme: {
        preset: MyPreset,
      },
    }),
    importProvidersFrom(CommonModule, BrowserModule, ApiModule, DialogsModule, FlexModule),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
    ApiInterceptor,
    API_INTERCEPTOR_PROVIDER,
    INIT_API_CONFIGURATION,
    CookieService,
    AuthGuard,
    AuthService,
    EventsService,
    HeaderService,
    RouterHelper,
    NotificationService,
    GoogleAnalyticsService,
    LocalNotificationsService,
    HelpscoutService,
    /*ZendeskService,*/
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
};
